import { CUSTOMER_FEATURE_KEYS, DEFAULT_CUSTOMER_FEATURES, } from './constants.js';
const KNOWN = new Set(CUSTOMER_FEATURE_KEYS);
/**
 * Turns whatever is stored on a tenant into the list the app can rely on.
 *
 * Lenient on read, strict on write. The write path validates against
 * `customerFeatureSchema` and rejects anything malformed; this path never rejects, because
 * it runs inside the tenant context endpoint — the request the whole customer app boots
 * from. A tenant row written by a newer deploy, or hand-edited in psql, must degrade to
 * "that feature is unknown here" rather than take the operator's entire booking site down.
 *
 * An empty or absent config expands to every feature on. See DEFAULT_CUSTOMER_FEATURES:
 * it means existing tenants gain these without a backfill, and switching one off is always
 * something somebody chose.
 *
 * A partial config is completed rather than treated as exhaustive. An operator who saved
 * before a new feature existed has a list that cannot mention it, and the alternative —
 * absent means hidden — would ship every future feature switched off for everyone who had
 * ever touched this screen.
 */
export function resolveCustomerFeatures(stored) {
    const configured = new Map();
    if (Array.isArray(stored)) {
        for (const entry of stored) {
            if (!entry || typeof entry !== 'object')
                continue;
            const { key, label, visible } = entry;
            if (typeof key !== 'string' || !KNOWN.has(key))
                continue;
            if (configured.has(key))
                continue; // first wins; duplicates cannot both apply
            const fallback = DEFAULT_CUSTOMER_FEATURES.find((f) => f.key === key);
            configured.set(key, {
                key: key,
                label: typeof label === 'string' && label.trim() ? label.trim() : (fallback?.label ?? key),
                // Anything that is not an explicit false leaves the feature on. A row missing the
                // flag is a row nobody decided about, and the default for those is on.
                visible: visible === false ? false : true,
            });
        }
    }
    // Configured entries keep the operator's order; anything they have never seen is appended
    // in the platform's order, so a new feature lands predictably rather than at the front.
    const ordered = [];
    for (const entry of Array.isArray(stored) ? stored : []) {
        const key = entry?.key;
        if (typeof key === 'string' && configured.has(key)) {
            const resolved = configured.get(key);
            if (!ordered.some((o) => o.key === resolved.key))
                ordered.push(resolved);
        }
    }
    for (const fallback of DEFAULT_CUSTOMER_FEATURES) {
        if (!ordered.some((o) => o.key === fallback.key))
            ordered.push({ ...fallback });
    }
    return ordered;
}
/** Whether a feature is switched on for this tenant. The single question callers ask. */
export function isCustomerFeatureEnabled(stored, key) {
    return resolveCustomerFeatures(stored).some((f) => f.key === key && f.visible);
}
//# sourceMappingURL=customer-features.js.map